import { ApiClient } from "@modules/core/api/client/apiClient"
import { CustomerHistoryDto, CustomerHistoryDtoSchema } from "@modules/finances/gateway/customerHistoryDto"
import { FinanceStatisticsDto, FinanceStatisticsDtoSchema } from "@modules/finances/gateway/financeStatisticsDto"
import {
  TransactionDetailsDto,
  TransactionDetailsDtoSchema,
  TransactionDto,
  TransactionDtoSchema,
} from "@modules/finances/gateway/transactionDto"
import { TransactionTotalsDto, TransactionTotalsDtoSchema } from "@modules/finances/gateway/transactionTotalsDto"
import { UserFinanceApiGateway } from "@modules/finances/gateway/userFinanceApiGateway"
import { TransactionStatus } from "@modules/finances/types/financesType"
import { StripeErrorsDto } from "@modules/finances/types/stripeErrorTypes"
import { PaginatedCursor } from "@modules/utils/paginated"

export class UserFinanceApi implements UserFinanceApiGateway {
  private userFinanceBaseUrl = "/financial"
  private userStripeErrorsUrl = "/stripe/errors"
  private userStripePaymentRetryUrl = "/stripe/payment/authentication"

  constructor(private api: ApiClient) {}

  async getTransaction(transactionId: string): Promise<TransactionDetailsDto> {
    const result = await this.api.get<TransactionDetailsDto>(`${this.userFinanceBaseUrl}/transactions/${transactionId}`)
    return TransactionDetailsDtoSchema.parse(result.data)
  }

  async refundTransaction(transactionId: string): Promise<void> {
    await this.api.post(`${this.userFinanceBaseUrl}/transactions/${transactionId}/refund`)
  }

  async getTransactions(filter: TransactionStatus | undefined, period: string, page?: string, size = 10) {
    const result = await this.api.get<PaginatedCursor<TransactionDto>>(`${this.userFinanceBaseUrl}/transactions`, {
      params: {
        page,
        pageSize: size,
        period,
        status: filter,
      },
    })

    const pageData = result.data
    return {
      ...pageData,
      data: pageData.data.map((transaction) => TransactionDtoSchema.parse(transaction)),
    }
  }

  async getTransactionsHistory(stripeCustomerId: string, page?: string, size = 10) {
    const result = await this.api.get<PaginatedCursor<CustomerHistoryDto>>(
      `${this.userFinanceBaseUrl}/customer/${stripeCustomerId}/history`,
      {
        params: {
          page,
          pageSize: size,
        },
      },
    )
    const pageData = result.data
    return {
      ...pageData,
      data: pageData.data.map((transaction) => CustomerHistoryDtoSchema.parse(transaction)),
    }
  }

  async getStats(period: string): Promise<FinanceStatisticsDto> {
    const result = await this.api.get<FinanceStatisticsDto>(`${this.userFinanceBaseUrl}/stats`, {
      params: {
        period,
      },
    })
    return FinanceStatisticsDtoSchema.parse(result.data)
  }

  async getTransactionTotals(period: string): Promise<TransactionTotalsDto> {
    const result = await this.api.get<TransactionTotalsDto>(`${this.userFinanceBaseUrl}/transaction-status-totals`, {
      params: {
        period,
      },
    })
    return TransactionTotalsDtoSchema.parse(result.data)
  }

  async getLastUpdate(): Promise<string> {
    const result = await this.api.get<string>(`${this.userFinanceBaseUrl}/last-update`)
    return result.data
  }

  async getFirstPeriod(): Promise<string> {
    const result = await this.api.get<string>(`${this.userFinanceBaseUrl}/first-period`)
    return result.data
  }

  async updateStats(): Promise<FinanceStatisticsDto> {
    const result = await this.api.post(`${this.userFinanceBaseUrl}/update-stats`)
    return FinanceStatisticsDtoSchema.parse(result.data)
  }

  async exportTransactions(startDate: string, endDate: string): Promise<string> {
    const result = await this.api.post<string>(
      `${this.userFinanceBaseUrl}/export-transactions?startDate=${startDate}&endDate=${endDate}`,
    )
    return result.data
  }

  async getStripeAccountErrors() {
    const result = await this.api.get<StripeErrorsDto | null>(`${this.userStripeErrorsUrl}`)
    return result.status === 204 ? null : result.data
  }

  async getPaymentAuthenticationUrl(
    returnUrl: string,
    programSubscriptionId?: string,
    programBundleSubscriptionId?: string,
    boxSubscriptionId?: string,
  ): Promise<string> {
    const result = await this.api.post<string>(`${this.userStripePaymentRetryUrl}`, {
      programSubscriptionId,
      programBundleSubscriptionId,
      boxSubscriptionId,
      returnUrl,
    })
    return result.data
  }
}
